import { useState, useCallback, useEffect, useRef } from "react";
import { LOYALTY_API_BASE, LOYALTY_AUTH_HEADER } from "../constants";

/**
 * Generic data-fetching hook for the Loyalty Engine API.
 * @param {string} endpoint  Path relative to LOYALTY_API_BASE (e.g. "/rewards?brand=foo")
 * @param {boolean} autoFetch Whether to fetch immediately on mount / endpoint change.
 */
export function useLoyaltyApi(endpoint, autoFetch = true) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const abortControllerRef = useRef(null);

    const fetchData = useCallback(async (overrideEndpoint) => {
        // Cancel any pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        // Guard: ignore non-string overrides (e.g. a click Event passed accidentally)
        const resolvedEndpoint =
            typeof overrideEndpoint === "string" ? overrideEndpoint : endpoint;
        const url = `${LOYALTY_API_BASE}${resolvedEndpoint}`;

        setLoading(true);
        setError(null);

        try {
            // Always include Basic Auth + extract brand for X-Brand header
            const headers = { ...LOYALTY_AUTH_HEADER };
            const urlObj = new URL(url, window.location.origin);
            const brand = urlObj.searchParams.get("brand");
            if (brand) {
                headers["X-Brand"] = brand;
            }

            const res = await fetch(url, { headers, signal: controller.signal });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            setData(json);
            return json;
        } catch (err) {
            if (err.name === 'AbortError') return null;
            setError(err.message);
            return null;
        } finally {
            // Only clear loading state if this is still the active request
            if (abortControllerRef.current === controller) {
                setLoading(false);
            }
        }
    }, [endpoint]);

    useEffect(() => {
        if (autoFetch && endpoint) {
            fetchData();
        }
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [autoFetch, endpoint, fetchData]);

    return { data, loading, error, refetch: fetchData };
}
